// src/pages/Home.jsx
import React from "react";
import { motion } from "framer-motion";
import Hero from "../components/Hero";
import {
  BriefcaseIcon,
  DevicePhoneMobileIcon,
  ShieldCheckIcon,
  CodeBracketIcon,
} from "@heroicons/react/24/outline";
import { NavLink } from "react-router-dom";
import LogoCanvas from "../components/LogoCanvas";

export default function Home() {
  const features = [
    {
      title: "Custom Software",
      icon: CodeBracketIcon,
      description:
        "Tailored solutions built to fit your business needs with scalable architecture.",
    },
    {
      title: "Mobile Applications",
      icon: DevicePhoneMobileIcon,
      description:
        "Responsive and intuitive mobile apps for iOS and Android platforms.",
    },
    {
      title: "Security & Compliance",
      icon: ShieldCheckIcon,
      description:
        "Industry-standard security practices to protect your data and ensure compliance.",
    },
    {
      title: "DevOps & Cloud",
      icon: BriefcaseIcon,
      description:
        "Robust cloud infrastructure and continuous delivery pipelines.",
    },
  ];

    const testimonials = [
    {
      quote: "FutureYX transformed our digital presence—our platform is more robust than ever.",
      author: "Jordan R., TechCorp",
    },
    {
      quote: "Their DevOps expertise saved us hours in deployment time each week.",
      author: "Alex K., FinancePro",
    },
    {
      quote: "We couldn’t be happier with our mobile app’s performance and UX.",
      author: "Dana S., HealthPlus",
    },
  ];

  const listVariant = {
    hidden: { opacity: 0, y: 20 },
    visible: (i = 1) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.2 },
    }),
  };

  return (
    <div>
      {/* Hero Section */}
      <Hero />

      {/* Features Section */}
      <section className="container mx-auto px-4 py-16">
        <motion.h3
          className="text-3xl font-bold text-center mb-8"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          What We Offer
        </motion.h3>
        <div className="grid gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {features.map(({ title, icon: Icon, description }, idx) => (
            <motion.div
              key={idx}
              className="bg-white rounded-lg shadow p-6 text-center"
              custom={idx}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={listVariant}
              whileHover={{ scale: 1.05 }}
            >
              <Icon className="w-12 h-12 mx-auto text-[#D7631F] mb-4" />
              <h4 className="text-xl font-semibold mb-2">{title}</h4>
              <p className="text-gray-600">{description}</p>
            </motion.div>
          ))}
        </div>
      </section>



      {/* Services Preview */}
      <section className="bg-gray-50 py-16">
        <div className="container mx-auto px-4">
          <h3 className="text-3xl font-bold text-center mb-8">Our Services</h3>
          <div className="flex flex-col md:flex-row md:-mx-4">
            <div className="md:w-1/3 px-4 mb-8 md:mb-0">
              <h4 className="text-xl font-semibold mb-2">
                Custom Software Development
              </h4>
              <p className="text-gray-600 mb-4">
                Building scalable web applications with modern technologies.
              </p>
              <NavLink to="/services" className="text-[#D7631F] hover:underline">
                Learn More →
              </NavLink>
            </div>
            <div className="md:w-1/3 px-4 mb-8 md:mb-0">
              <h4 className="text-xl font-semibold mb-2">
                Mobile App Development
              </h4>
              <p className="text-gray-600 mb-4">
                Creating user-friendly mobile experiences for all devices.
              </p>
              <NavLink to="/services" className="text-[#D7631F] hover:underline">
                Learn More →
              </NavLink>
            </div>
            <div className="md:w-1/3 px-4">
              <h4 className="text-xl font-semibold mb-2">
                Cloud & DevOps Solutions
              </h4>
              <p className="text-gray-600 mb-4">
                Optimizing deployments and infrastructure for efficiency.
              </p>
              <NavLink to="/services" className="text-[#D7631F] hover:underline">
                Learn More →
              </NavLink>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="container mx-auto px-4 py-16">
        <div className="md:flex md:items-center md:-mx-4">
          <div className="md:w-1/2 px-4 mb-8 md:mb-0">
            <h3 className="text-3xl font-bold mb-4">About FutureYX</h3>
            <p className="text-gray-600 mb-6">
              FutureYX is a pioneering IT solutions provider dedicated to developing
              cutting-edge software and mobile applications. With expertise in cloud
              computing, DevOps, data security, and UI/UX design, we partner with businesses
              to deliver scalable, secure, and user-friendly digital products.
            </p>
            <NavLink to="/about" className="bg-[#D7631F] text-white px-6 py-3 rounded-full hover:bg-[#BF5C1A]">
              Learn More About Us
            </NavLink>
          </div>
          <div className="md:w-1/2 px-4">
            <img
              src="/about-image.jpg"
              alt="About FutureYX"
              className="rounded-lg shadow-md w-full"
            />
          </div>
        </div>
      </section>

            <section className="bg-gray-100 py-16">
        <div className="container mx-auto px-4">
          <h3 className="text-3xl font-bold text-center mb-8">
            What Our Clients Say
          </h3>
          <div className="grid gap-8 md:grid-cols-3">
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                className="bg-white p-6 rounded-lg shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.3 }}
              >
                <p className="italic mb-4">“{t.quote}”</p>
                <p className="font-semibold text-right">— {t.author}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="bg-[#D7631F] text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h3 className="text-3xl font-bold mb-4">Ready to Start Your Project?</h3>
          <p className="mb-6">Get in touch with our team and let’s build something great together.</p>
          <NavLink to="/contact" className="bg-white text-[#D7631F] font-semibold px-6 py-3 rounded-full hover:bg-gray-100">
            Contact Us
          </NavLink>
        </div>
      </section>
    </div>
  );
}